import React, { useState, lazy, Suspense } from 'react';
import { Button, Popover, Checkbox, Modal, Spin, Typography, notification } from 'antd';
import { FileTextOutlined, PrinterOutlined } from '@ant-design/icons';

import billingService from '../../../services/billingService';

const OrderDocumentsPdf = lazy(() => import('./OrderDocumentsPdf'));

const { Text } = Typography;

const OrderDocumentsPanel = ({ order, primary }) => {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState([]);
  const [printing, setPrinting] = useState(false);
  const [pdf, setPdf] = useState(null);

  const invoice = order?.invoices?.[0] || null;
  // El fito es del despacho ACTIVE: sin él no hay constancia que imprimir.
  const dispatch = order?.active_dispatch || null;

  const handleOpenChange = (nextOpen) => {
    // Se preselecciona al abrir y no al cargar: si la orden se factura con el
    // panel cerrado, la próxima vez ya aparece marcada la factura.
    if (nextOpen) {
      const documents = [...(invoice ? ['invoice'] : []), ...(dispatch ? ['fito'] : [])];
      setSelected(documents.length > 0 ? documents : ['order']);
    }
    setOpen(nextOpen);
  };

  const toggle = (key) => (e) =>
    setSelected((prev) => (e.target.checked ? [...prev, key] : prev.filter((k) => k !== key)));

  const handlePrint = async () => {
    setPrinting(true);
    try {
      const [invoiceData, fitoData] = await Promise.all([
        selected.includes('invoice') ? billingService.obtenerFacturaPdf(invoice.id).then((r) => r?.data) : null,
        selected.includes('fito') ? billingService.obtenerFitoPdf(dispatch.id).then((r) => r?.data) : null,
      ]);
      setPdf({ order: selected.includes('order') ? order : null, invoiceData, fitoData });
      setOpen(false);
    } catch (error) {
      notification.error({
        title: 'No se pudieron cargar los documentos',
        description: error?.response?.data?.mensaje || 'Error inesperado.',
      });
    } finally {
      setPrinting(false);
    }
  };

  const content = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, width: 260 }}>
      <Checkbox checked={selected.includes('order')} onChange={toggle('order')}>
        Documento de venta
      </Checkbox>
      <div>
        <Checkbox
          disabled={!dispatch}
          checked={selected.includes('fito')}
          onChange={toggle('fito')}
        >
          {dispatch?.fito_display ? `Constancia fitosanitaria ${dispatch.fito_display}` : 'Constancia fitosanitaria'}
        </Checkbox>
        {!dispatch && (
          <div><Text type="secondary" style={{ fontSize: 12, marginLeft: 24 }}>La orden no tiene despacho activo</Text></div>
        )}
      </div>
      <div>
        <Checkbox
          disabled={!invoice}
          checked={selected.includes('invoice')}
          onChange={toggle('invoice')}
        >
          {invoice ? `Factura ${invoice.display_number}` : 'Factura'}
        </Checkbox>
        {!invoice && (
          <div><Text type="secondary" style={{ fontSize: 12, marginLeft: 24 }}>La orden aún no está facturada</Text></div>
        )}
      </div>
      <Button
        type="primary"
        icon={<PrinterOutlined />}
        disabled={selected.length === 0}
        loading={printing}
        onClick={handlePrint}
      >
        Imprimir seleccionados
      </Button>
    </div>
  );

  return (
    <>
      <Popover
        trigger="click"
        placement="bottomRight"
        title="Documentos"
        content={content}
        open={open}
        onOpenChange={handleOpenChange}
      >
        <Button type={primary ? 'primary' : 'default'} icon={<FileTextOutlined />} disabled={!order}>
          Documentos
        </Button>
      </Popover>

      <Modal
        open={Boolean(pdf)}
        onCancel={() => setPdf(null)}
        footer={null}
        width={900}
        destroyOnHidden
        title={order ? `Orden ${order.display_number}` : 'Orden de venta'}
      >
        {pdf && (
          <Suspense fallback={<div style={{ padding: 48, textAlign: 'center' }}><Spin /></div>}>
            <OrderDocumentsPdf order={pdf.order} invoiceData={pdf.invoiceData} fitoData={pdf.fitoData} />
          </Suspense>
        )}
      </Modal>
    </>
  );
};

export default OrderDocumentsPanel;
